import { useCallback } from "react";

import { useSyncPatientByEhr } from "@elation/api-client/src/stitch-web";
import type { PatientSearchItem } from "@elation/api-client/src/stitch-web/models";

export class PatientSearchResultUnassignableError extends Error {
  constructor() {
    super("This patient search result cannot be assigned.");
    this.name = "PatientSearchResultUnassignableError";
  }
}

/**
 * Resolves a patient search result to a Stitch patient id, syncing external EHR
 * results first when the search result has not been imported yet.
 */
export function useResolvePatientId() {
  const syncPatientByEhr = useSyncPatientByEhr();

  const resolvePatientId = useCallback(
    async (patient: PatientSearchItem): Promise<number> => {
      if (patient.patientId != null) {
        return patient.patientId;
      }

      if (!patient.ehr || patient.externalPatientId == null) {
        throw new PatientSearchResultUnassignableError();
      }

      const result = await syncPatientByEhr.mutateAsync({
        ehr: patient.ehr,
        patientId: String(patient.externalPatientId),
      });
      const patientId = result.data.patient.id;

      if (patientId == null) {
        throw new Error("The synced patient did not include an id.");
      }

      return patientId;
    },
    [syncPatientByEhr],
  );

  return {
    isResolvingPatientId: syncPatientByEhr.isPending,
    resolvePatientId,
  } as const;
}
